import React, { useContext, useMemo, useState } from 'react';
import {
  Box,
  Button,
  IconName,
  Tooltip,
} from '@usealloy/component-library';
import type { DecisionTypes } from '@usealloy/typegen';
import { DecisionTypesMap } from '@usealloy/typegen';

import JourneyApplicationContext from '~/application-review/context';
import { UserContext } from '~/dashboard/context';
import WatchlistReview from '~/entities/evaluations/watchlist-review/WatchlistReview';
import { SideviewPanel } from '~/entities/evaluations/EvaluationTabs/SideviewPanel';
import { WatchlistAgentPanel } from '~/entities/evaluations/EvaluationTabs/WatchlistAgentPanel/WatchlistAgentPanel';

import { useEventTypeEvaluationsContext } from '../context';

interface WatchlistMatchesProps {
  evaluation: {
    decision_type?: DecisionTypes;
    entity_token: string;
    evaluation_token: string;
  };
}

const WatchlistMatches = ({ evaluation }: WatchlistMatchesProps) => {
  const { featureFlags } = useContext(UserContext);
  const { isInvestigationPage } = useEventTypeEvaluationsContext();
  const { currentAgentIsAssigned, isAlertInTerminalState } = useContext(JourneyApplicationContext);
  const [isAgentPanelOpen, setIsAgentPanelOpen] = useState(false);

  const canAgentResolve = useMemo(() => {
    if (isInvestigationPage && isAlertInTerminalState) return false;
    if (evaluation.decision_type === DecisionTypesMap.INFORMATIONAL) return false;
    return currentAgentIsAssigned;
  }, [
    currentAgentIsAssigned,
    evaluation.decision_type,
    isInvestigationPage,
    isAlertInTerminalState,
  ]);

  return (
    <>
      {featureFlags?.llmWatchlistAgent && (
        <Box
          direction="row"
          style={{
            width: 'fit-content',
            marginLeft: 'auto',
            marginBottom: '-45px',
            // This element overlaps the Tabs header area; ensure it sits above and receives pointer events.
            position: 'relative',
            zIndex: 10,
          }}
        >
          <Tooltip content="Open AI Watchlist Screening">
            <Button
              icon={IconName.WandMagicSparkles}
              onClick={() => {
                setIsAgentPanelOpen(!isAgentPanelOpen);
              }}
              style={{ backgroundColor: 'var(--cl-purple-100)' }}
              data-dd-action-name="alloydd-ai-watchlist-triggered"
            />
          </Tooltip>
        </Box>
      )}

      <WatchlistReview
        canAgentResolve={canAgentResolve}
        entityToken={evaluation.entity_token}
        evaluationToken={evaluation.evaluation_token}
        showLatestDecisions
      />

      {isAgentPanelOpen && (
        <SideviewPanel
          onClose={() => setIsAgentPanelOpen(false)}
          panelTitle="Watchlist Screening"
          collapsedTabTooltip="Open Watchlist Screening"
        >
          <WatchlistAgentPanel
            entityToken={evaluation.entity_token}
            evaluationToken={evaluation.evaluation_token}
            onClose={() => setIsAgentPanelOpen(false)}
          />
        </SideviewPanel>
      )}
    </>
  );
};

export default WatchlistMatches;
